import React, { useCallback, useEffect, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import { UserFormServiceButtonClose, UserFormServiceButtonCreated } from "../../MyBooking/styled/DesStyled";
import {
	getStationStatusOptions,
	changeStationStatus,
	StatusOption,
	StatusOptionsResponse,
	StatusBlocker,
} from "../../../../services/PartnerService";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";

/**
 * Hộp thoại "Change Status" — MỘT CỬA thay cho 3 mục rời trong menu Actions
 * (Activate/Deactivate · Ban station · Unban station).
 *
 * Quân chốt 24/08/2026: gom về một nút, sổ xuống 5 trạng thái, mỗi cái kèm điều kiện,
 * cái nào không thoả thì hiện cảnh báo để xử lý.
 *
 * Quân chốt 25/08/2026, hai điều đổi cách viết màn này:
 *   · TIẾNG ANH, và nhãn trùng khít giá trị lưu trong DB (`actived` → "Actived", không
 *     phải "Active") — để người soi dữ liệu không phải dịch ngược, chỗ dễ sai nhất.
 *   · BỎ hết tên hành động (Publish/Ban/Deactive). Admin chỉ nhìn TRẠNG THÁI và sửa
 *     TRẠNG THÁI; không có động từ nào trong danh sách.
 *
 * Luật hiển thị: hộp thoại KHÔNG tự chấm điều kiện. Nó gọi `/status-options` và vẽ
 * đúng những gì server trả về. Một nơi giữ luật thì không bao giờ có cảnh "giao diện
 * bảo được, bấm vào lại bị chặn".
 */

/** Mã lý do chặn → câu chữ cho người vận hành. Server chỉ trả mã, câu chữ nằm ở đây. */
function moTaChan(b: StatusBlocker): string {
	const tien = (n?: number) => (n || 0).toLocaleString("en-US");
	switch (b.code) {
		case "same_status":
			return "Station is already in this status";
		case "no_return_to_pending":
			return "Cannot go back to Pending — a station only moves forward";
		case "missing_coordinates":
			return "No coordinates yet — guests would not see this station on the map";
		case "has_debt":
			return b.owedToStation
				? `WhaleLO still owes this station ${tien(
						b.owedToStation
				  )} — once Inactive they cannot log in to withdraw`
				: `Station still owes WhaleLO ${tien(b.owedToWhalelo)}`;
		case "has_open_orders":
			return `${b.count} paid order(s) still open`;
		case "luggage_in_storage":
			return `${b.count} order(s) still have luggage at the station, before the pick-up date`;
		case "reason_required":
			return `A reason is required, at least ${b.minLength} characters`;
		default:
			return b.code;
	}
}

const MAU: Record<string, { nen: string; chu: string }> = {
	pending: { nen: "#EDE9FB", chu: "#4A3E8C" },
	actived: { nen: "#FBEBD6", chu: "#96530A" },
	published: { nen: "#DEF0E8", chu: "#14704F" },
	inactive: { nen: "#E7EAEF", chu: "#5A6779" },
	banned: { nen: "#FADEDE", chu: "#9E2222" },
};

function Chip(props: { status: string; label: string }) {
	const m = MAU[props.status] || MAU.inactive;
	return (
		<span
			style={{
				background: m.nen,
				color: m.chu,
				fontSize: 11,
				fontWeight: 700,
				padding: "3px 9px",
				borderRadius: 99,
				whiteSpace: "nowrap",
			}}>
			{props.label}
		</span>
	);
}

export default function DialogStationStatus(props: {
	openDialog: boolean;
	setOpenDialog: (v: boolean) => void;
	partnerId: string | null;
	reload: () => void;
}) {
	const { openAlertDialog } = useAlertDialog();
	const [data, setData] = useState<StatusOptionsResponse | null>(null);
	const [dangTai, setDangTai] = useState(false);
	const [chon, setChon] = useState<string>("");
	const [lyDo, setLyDo] = useState("");
	const [banKhan, setBanKhan] = useState(false);
	const [lyDoKhan, setLyDoKhan] = useState("");
	const [dangGui, setDangGui] = useState(false);

	const nap = useCallback(async () => {
		if (!props.partnerId) return;
		setDangTai(true);
		try {
			setData(await getStationStatusOptions(props.partnerId));
		} catch {
			openAlertDialog?.(AlertType.Fail, "Could not read the station status.");
			props.setOpenDialog(false);
		} finally {
			setDangTai(false);
		}
	}, [props.partnerId]);

	// Nạp lại MỖI LẦN mở — không dùng lại dữ liệu cũ. Công nợ và số đơn đổi liên tục.
	useEffect(() => {
		if (!props.openDialog) return;
		setChon("");
		setLyDo("");
		setBanKhan(false);
		setLyDoKhan("");
		void nap();
	}, [props.openDialog, nap]);

	const opt: StatusOption | undefined = data?.options.find((o) => o.status === chon);

	// Chấm lại phía giao diện CHỈ để bật/tắt nút — server vẫn là nơi quyết định.
	const chanConLai = (opt?.blockers || []).filter((b) => {
		if (b.code === "reason_required") return lyDo.trim().length < (b.minLength || 20);
		if (b.code === "luggage_in_storage" && banKhan && lyDoKhan.trim().length >= 30) return false;
		return true;
	});
	const choBam = !!opt && chanConLai.length === 0 && !dangGui;

	const xacNhan = async () => {
		if (!props.partnerId || !opt) return;
		setDangGui(true);
		try {
			await changeStationStatus(props.partnerId, {
				status: opt.status,
				reason: lyDo.trim() || undefined,
				force: banKhan || undefined,
				forceReason: lyDoKhan.trim() || undefined,
			});
			openAlertDialog?.(AlertType.Success, `Status changed to "${opt.label}".`);
			props.reload();
			props.setOpenDialog(false);
		} catch (e: any) {
			// 422 kèm blockers = điều kiện đổi giữa lúc mở và lúc bấm. Nạp lại để người
			// dùng thấy tình hình mới, thay vì hiện một câu "thất bại" rồi thôi.
			const bl = e?.response?.data?.blockers as StatusBlocker[] | undefined;
			openAlertDialog?.(
				AlertType.Fail,
				bl?.length
					? `Cannot change status: ${bl.map(moTaChan).join(" · ")}`
					: e?.response?.data?.message || "Failed to change status."
			);
			void nap();
		} finally {
			setDangGui(false);
		}
	};

	return (
		<DialogWrapMedium
			login={true}
			open={props.openDialog}
			onClose={() => props.setOpenDialog(false)}
			title="Change Status"
			actions={
				<>
					<UserFormServiceButtonClose
						type="button"
						onClick={() => props.setOpenDialog(false)}>
						Cancel
					</UserFormServiceButtonClose>
					<UserFormServiceButtonCreated
						type="button"
						disabled={!choBam}
						style={choBam ? undefined : { opacity: 0.45, cursor: "not-allowed" }}
						onClick={() => void xacNhan()}>
						{dangGui ? "Saving…" : "Confirm"}
					</UserFormServiceButtonCreated>
				</>
			}>
			{dangTai || !data ? (
				<div style={{ padding: 20, color: "#5A6779" }}>Reading status…</div>
			) : (
				<div style={{ fontSize: 14, lineHeight: 1.6 }}>
					<div style={{ marginBottom: 14, color: "#5A6779" }}>
						<strong style={{ color: "#141A24" }}>{data.partnerName}</strong> — currently{" "}
						<Chip
							status={data.current}
							label={data.currentLabel}
						/>
					</div>

					<div
						style={{
							fontSize: 11,
							fontWeight: 700,
							letterSpacing: ".08em",
							color: "#74839A",
							marginBottom: 6,
						}}>
						CHANGE TO
					</div>
					<div style={{ display: "grid", gap: 6 }}>
						{data.options.map((o) => {
							const daChon = o.status === chon;
							const chanCung = o.blockers.some(
								(b) => b.code === "same_status" || b.code === "no_return_to_pending"
							);
							return (
								<button
									key={o.status}
									type="button"
									disabled={chanCung}
									onClick={() => setChon(o.status)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										textAlign: "left",
										font: "inherit",
										padding: "9px 12px",
										borderRadius: 8,
										cursor: chanCung ? "not-allowed" : "pointer",
										opacity: chanCung ? 0.4 : 1,
										border: `1px solid ${daChon ? "#16467E" : "#D6DEE8"}`,
										background: daChon ? "#E3ECF8" : "#fff",
									}}>
									<Chip
										status={o.status}
										label={o.label}
									/>
									{/* CHỈ dòng bị chặn mới có chữ. Quân chốt 25/08 bỏ nhãn "đủ điều kiện":
									    dòng bấm được đã tự nói lên rồi, ba dòng cùng ghi xanh chỉ làm loãng
									    đúng cái dòng cần đập vào mắt. Dòng không đi được thì làm mờ là đủ. */}
									{!o.allowed && !chanCung && (
										<span style={{ fontSize: 12, color: "#96530A" }}>
											{o.blockers.length} condition(s) to resolve
										</span>
									)}
								</button>
							);
						})}
					</div>

					{opt && (
						<div style={{ marginTop: 14 }}>
							{opt.blockers.length > 0 && (
								<div
									style={{
										background: chanConLai.length ? "#FADEDE" : "#DEF0E8",
										border: `1px solid ${chanConLai.length ? "#E3A0A0" : "#9CCFBB"}`,
										borderRadius: 9,
										padding: "11px 13px",
									}}>
									<div
										style={{
											fontWeight: 700,
											marginBottom: 6,
											color: chanConLai.length ? "#9E2222" : "#14704F",
										}}>
										{chanConLai.length
											? `${chanConLai.length} condition(s) left to resolve`
											: "All conditions met"}
									</div>
									{opt.blockers.map((b, i) => {
										const conChan = chanConLai.some((x) => x.code === b.code);
										return (
											<div
												key={i}
												style={{ display: "flex", gap: 8, color: "#47566B", marginBottom: 3 }}>
												<span>{conChan ? "✕" : "✓"}</span>
												<span>{moTaChan(b)}</span>
											</div>
										);
									})}
								</div>
							)}

							{opt.needsReason && (
								<div style={{ marginTop: 12 }}>
									<div
										style={{
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: ".08em",
											color: "#74839A",
											marginBottom: 5,
										}}>
										BAN REASON (required)
									</div>
									<textarea
										value={lyDo}
										onChange={(e) => setLyDo(e.target.value)}
										rows={2}
										placeholder="State the violation — this reason goes into the audit log for others to read later"
										style={{
											width: "100%",
											font: "inherit",
											padding: 9,
											borderRadius: 8,
											border: "1px solid #D6DEE8",
										}}
									/>
								</div>
							)}

							{opt.canForce && (
								<div style={{ marginTop: 11, borderTop: "1px solid #E6ECF3", paddingTop: 11 }}>
									<label
										style={{
											display: "flex",
											gap: 8,
											cursor: "pointer",
											alignItems: "flex-start",
										}}>
										<input
											type="checkbox"
											checked={banKhan}
											onChange={(e) => setBanKhan(e.target.checked)}
										/>
										<span style={{ color: "#9E2222", fontWeight: 600 }}>
											Force ban — accept that guests still have luggage at the station
										</span>
									</label>
									{banKhan && (
										<textarea
											value={lyDoKhan}
											onChange={(e) => setLyDoKhan(e.target.value)}
											rows={2}
											placeholder="Why this station must be banned now despite luggage on site (at least 30 characters)"
											style={{
												width: "100%",
												font: "inherit",
												padding: 9,
												marginTop: 7,
												borderRadius: 8,
												border: "1px solid #E3A0A0",
											}}
										/>
									)}
								</div>
							)}

							{opt.effects.length > 0 && chanConLai.length === 0 && (
								<div style={{ marginTop: 12, fontSize: 12.5, color: "#74839A" }}>
									Will write:{" "}
									{opt.effects
										.map((e) => (e.note ? e.note : `${e.field} = ${String(e.to)}`))
										.join(" · ")}
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</DialogWrapMedium>
	);
}
